import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import { useClient } from "../context/ClientContext";

import ClientSidebar from "../components/clients/ClientSidebar";
import ClientHeader from "../components/clients/ClientHeader";

/*
 * =========================================================
 * PROFITYO CLIENT PORTAL LAYOUT
 *
 * Dedicated client workspace.
 *
 * Features:
 * - ClientHeader
 * - ClientSidebar
 * - Responsive layout
 * - Collapsible sidebar
 * - Independent dashboard scrolling
 * - Fixed-height AI Copilot layout
 * - Mobile sidebar support
 * - Organization-scoped workspace guard
 *
 * The accounting firm layout is not affected.
 * =========================================================
 */

export default function ClientLayout() {
  const location = useLocation();

  const {
    user,
    activeWorkspace,
    workspaceType,
  } = useClient();

  /* =======================================================
     UI STATE
  ======================================================= */

  const [
    isSidebarOpen,
    setIsSidebarOpen,
  ] = useState(false);

  const [
    isSidebarCollapsed,
    setIsSidebarCollapsed,
  ] = useState(false);

  const mainRef = useRef(null);

  /* =======================================================
     CURRENT PAGE
  ======================================================= */

  const isAIPage =
    location.pathname === "/client/ai";

  const scopeKey =
    user?.id && activeWorkspace?.id
      ? `${user.id}:${activeWorkspace.id}`
      : null;

  /* =======================================================
     CLOSE MOBILE SIDEBAR ON NAVIGATION
  ======================================================= */

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname, scopeKey]);

  /* =======================================================
     RESET CLIENT CONTENT SCROLL ON NAVIGATION

     Only resets the main client page container.
     Does not affect the AI chat's internal scroll.
  ======================================================= */

  useEffect(() => {
    if (!isAIPage && mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname, scopeKey, isAIPage]);

  /* =======================================================
     CLIENT WORKSPACE GUARD

     ClientContext provides the verified active
     organization and workspace type.

     RLS and Edge Functions enforce backend
     organization-level authorization.
  ======================================================= */

  if (
    workspaceType !== "client" ||
    !activeWorkspace?.id
  ) {
    return (
      <Navigate
        to="/"
        replace
      />
    );
  }

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="relative h-screen h-[100dvh] w-full overflow-hidden bg-slate-50">

      {/* ===================================================
          CLIENT SIDEBAR
      =================================================== */}

      <ClientSidebar
        isOpen={isSidebarOpen}
        onClose={() =>
          setIsSidebarOpen(false)
        }
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
      />

      {/* ===================================================
          CLIENT MAIN APPLICATION AREA
      =================================================== */}

      <div
        className={`flex h-full min-h-0 min-w-0 flex-col transition-all duration-300 ${
          isSidebarCollapsed
            ? "md:ml-20"
            : "md:ml-72"
        }`}
      >

        {/* =================================================
            CLIENT HEADER

            Always visible at the top of the
            Client Portal.
        ================================================= */}

        <ClientHeader
          onMenuClick={() =>
            setIsSidebarOpen(true)
          }
        />

        {/* =================================================
            CLIENT PAGE CONTENT

            Dashboard:
            - Scrolls vertically inside <main>
            - Header stays visible
            - Sidebar stays independent

            AI Copilot:
            - Fixed content container
            - Chat component manages scrolling
        ================================================= */}

        <main
          ref={mainRef}
          className={
            isAIPage
              ? [
                  "flex",
                  "min-h-0",
                  "min-w-0",
                  "flex-1",
                  "flex-col",
                  "overflow-hidden",
                ].join(" ")
              : [
                  "min-h-0",
                  "min-w-0",
                  "flex-1",
                  "overflow-y-auto",
                  "overflow-x-hidden",
                  "overscroll-y-contain",
                  "px-4",
                  "py-6",
                  "md:px-6",
                  "md:py-8",
                  "lg:px-8",
                  "lg:py-10",
                ].join(" ")
          }
        >
          <Outlet />
        </main>

      </div>
    </div>
  );
}